import React, { useEffect, useMemo, useState } from "react";
import HeroSection from "../../../Components/Common/HeroSection";
import useSWR from "swr";
import {
  orderUrls,
  StaticPageApiUrls,
  UserApiUrls,
} from "../../../Api/Auth/ApiUrls";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../Context/AuthContext";
import {
  customFetcher,
  fetcher,
  sendPostRequestAuth,
} from "../../../Api/swrConfig";
import Button from "../../../Components/Common/Button";
import { Loader } from "../../../Components/Common/Loader";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../Components/Common/Modal";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import RoutineView from "../Component/RoutineView";
import { publicUrls, studentUrls } from "../../../Api/Auth/AppUrls";
import { motion } from "framer-motion";
import Input from "../../../Components/Common/Input";
import useToastError from "../../../hooks/useErrorToast";

const stripePromise = loadStripe(process.env.REACT_APP_Publishable_Key);
const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a" },
  },
};

const PaymentForm = ({ clientSecret, onClose, mutate, item }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [saveCard, setSaveCard] = useState(false);

  const { data } = useSWR(UserApiUrls.savedCards, fetcher);
  const savedCards = data?.cards || [];

  const { trigger } = useSWRMutation(orderUrls.payment, sendPostRequestAuth, {
    onSuccess: (data) => {
      mutate();
      toast.success(
        "Congratulations! You got access to Mechanical Subscription Materials & Practice Hub (Mechanical) for 6 Months!"
      );
      toast.info(`You can access your tests from your student dashboard too`);
    },
    onError: (error) => {
      toast.error(error.message || "Payment failed. Please try again.");
    },
  });
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    // Get the card number element explicitly
    try {
      let paymentMethodId;

      if (selectedCard) {
        paymentMethodId = selectedCard; // Use selected saved card
      } else {
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
          toast.error("Card details not found. Please try again.");
          setIsProcessing(false);
          return;
        }

        // Create a payment method using the card details
        const { paymentMethod, error: pmError } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });

        if (pmError) {
          toast.error(pmError.message || "Failed to create payment method.");
          setIsProcessing(false);
          return;
        }
        paymentMethodId = paymentMethod.id;
      }
      // Confirm payment
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId,
          setup_future_usage: "off_session",
        });

      if (confirmError) {
        toast.error(
          confirmError.message || "Payment failed. Please try again."
        );
      } else if (paymentIntent?.status === "succeeded") {
        toast.success("Payment successful!");

        // Send request to backend to update order status
        await trigger({
          paymentIntentId: paymentIntent.id,
          itemName: item?.title || "Live Class (Mechanical)",
          saveCard: saveCard,
        });

        onClose();
      }
    } catch (err) {
      console.error("Payment error:", err);
      toast.error("An unexpected error occurred.");
    }

    setIsProcessing(false);
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg p-3">
      {savedCards.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Saved Cards
          </label>
          <select
            className="p-2 border rounded-md w-full"
            value={selectedCard || ""}
            onChange={(e) => setSelectedCard(e.target.value || null)}
          >
            <option value="">Use a new card</option>
            {savedCards.map((card) => (
              <option key={card.id} value={card.id}>
                {`**** **** **** ${card.card.last4
                  } (${card.card.brand.toUpperCase()})`}
              </option>
            ))}
          </select>
        </div>
      )}

      {!selectedCard && (
        <>
          <label className="block text-sm font-medium text-gray-700">
            Card Number
          </label>
          <CardNumberElement
            className="p-2 border rounded-md"
            options={cardElementOptions}
          />

          <div className="flex space-x-4">
            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                Expiry Date
              </label>
              <CardExpiryElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>

            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                CVC
              </label>
              <CardCvcElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="saveCard"
              checked={saveCard}
              onChange={() => setSaveCard(!saveCard)}
              className="h-4 w-4"
            />
            <label htmlFor="saveCard" className="text-sm text-gray-700">
              Save this card for future payments
            </label>
          </div>
        </>
      )}

      <Button type="primary" className="mt-4 w-full" disabled={isProcessing}>
        {isProcessing ? "Processing..." : "Pay Now"}
      </Button>
    </form>
  );
};

const getLiveClassStatus = (startDateStr, endDateStr) => {
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  const now = new Date();

  // Format the date without the weekday
  const formatDate = (date) =>
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  if (now < startDate) {
    return `Starting on ${formatDate(startDate)}`;
  } else if (now >= startDate && now <= endDate) {
    return `Classes ongoing until ${formatDate(endDate)}`;
  } else {
    return `Classes finished on ${formatDate(endDate)}`;
  }
};

const normalizeImages = (image) => {
  if (!image) return [];
  if (Array.isArray(image)) return image.filter(Boolean);
  if (typeof image === "string") {
    const trimmed = image.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch (error) {
        return trimmed ? [trimmed] : [];
      }
    }
    if (trimmed.startsWith("{")) {
      try {
        const parsedObject = JSON.parse(trimmed);
        if (Array.isArray(parsedObject)) return parsedObject.filter(Boolean);
      } catch (error) {
        return trimmed ? [trimmed] : [];
      }
    }
    return trimmed ? [trimmed] : [];
  }
  return [];
};

const LiveClassMechanical = () => {
  const location = useLocation();
  const { showErrorToast } = useToastError();
  const { token, userData } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [selected, setSelected] = useState(null);
  const [paymentConfirmScreen, setPaymentConfirmScreen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const slug = location.pathname.replace("mechanical/", "mechanical-");
  const [routineModal, setRoutineModal] = useState(null);
  const { data, isLoading, mutate } = useSWR(
    slug ? StaticPageApiUrls.info(slug) : null,
    (url) => customFetcher(url, token)
  );
  const navigate = useNavigate();

  const { trigger: promoTrigger } = useSWRMutation(
    orderUrls.promo,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        const calculatedPrice = selected.price - (selected.price * data?.discount) / 100;
        setDiscountedPrice(parseFloat(calculatedPrice.toFixed(2)));
        setAppliedPromo(promoCode);
        toast.success(`Promo Code applied successfully`);
      },
      onError: (error) => {
        console.log(error);
        showErrorToast(error || "Invalid promo code. Please try again.");
      },
    }
  );

  const onBuyClick = (item) => {
    setSelected(item);
    setPaymentConfirmScreen(true);
    setIsModalOpen(true);
  };

  const onBuy = async (item) => {
    const payload = {
      userId: userData?._id,
      liveClassSubscriptionId: item._id,
      promo: discountedPrice ? appliedPromo : undefined,
      slug: slug,
      customer: {
        id: userData?._id,
        name: userData?.name,
        email: userData?.email,
      },
    };
    await purchaseItem(payload);
  };

  const handleApplyPromo = async (e) => {
    e.preventDefault();
    if (!promoCode) {
      return toast.error("Enter Promo Code");
    }
    await promoTrigger({
      promoCode: promoCode,
      slug: slug,
      price: selected.price,
    });
  };
  const { trigger: purchaseItem, isMutating } = useSWRMutation(
    orderUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Processing payment...");
        setClientSecret(data.clientSecret);
        setPaymentConfirmScreen(false);
        // setIsModalOpen(true);
      },
      onError: (error) => {
        toast.error(error.message || "Payment failed. Please try again.");
      },
    }
  );

  const images = useMemo(
    () => normalizeImages(data?.data?.image),
    [data?.data?.image]
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [images.length]);

  const hasMultipleImages = images.length > 1;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 1, y: -40 }} // Start above the view
      whileInView={{ opacity: 1, y: 0 }} // End at normal position
      transition={{ duration: 1, ease: "easeOut" }} // Longer transition for more emphasis
    >
      <HeroSection
        title={
          data?.data?.displayTitle || data?.data?.title || "Our Live Classes"
        }
        backgroundImage="https://storage.googleapis.com/road-to-fe/uploads/1741417535659-07d447cd-82a2-4b1b-8c08-9fffeaaefa18-colleagues-doing-team-work-project_23-2149361607.avif"
        subtitle={data?.data?.headerDescription || "One Platform Many Courses"}
      />
      <div className="gap-3 container mx-auto sm:mt-12 mt-4 flex justify-center md:justify-between items-start flex-wrap md:flex-nowrap px-2">
        <div
          className="max-w-5xl w-full"
          dangerouslySetInnerHTML={{ __html: data?.data?.bodyDescription }}
        />
        <div className="relative w-full max-w-2xl">
          {images.length > 0 && (
            <>
              <div className="w-full h-[32rem] md:h-[36rem] lg:h-[40rem] bg-gray-100 rounded-2xl shadow-xl flex items-center justify-center overflow-hidden">
                <img
                  src={images[currentImageIndex]}
                  alt="Live Class"
                  className="max-h-full max-w-full object-contain transition-all duration-500"
                />
              </div>
              {hasMultipleImages && (
                <>
                  <button
                    className="absolute top-1/2 -translate-y-1/2 left-4 bg-black/60 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-black/80 transition"
                    onClick={handlePrevImage}
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    className="absolute top-1/2 -translate-y-1/2 right-4 bg-black/60 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-black/80 transition"
                    onClick={handleNextImage}
                    aria-label="Next image"
                  >
                    ›
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
                    {images.map((_, index) => (
                      <span
                        key={index}
                        className={`w-3 h-3 rounded-full border border-white/80 shadow ${currentImageIndex === index
                          ? "bg-primary"
                          : "bg-black/80"
                          }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <h2 className="text-3xl font-semibold text-primary text-center mt-4">
        {`Try our ${data?.data?.displayTitle || data?.data?.title || "Live Classes"
          }!`}
      </h2>
      <div className="mt-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded text-center max-w-xl mx-auto">
        <p className="text-base font-medium">
          🎉 <span className="font-bold">Hurry! Limited Time Offer:</span> Get{" "}
          <span className="font-semibold">FREE</span> access to{" "}
          <Link className="italic" to={publicUrls.feSubscriptionMechanical}>
            Mechanical Subscription Materials
          </Link>{" "}
          <span className="font-semibold">&</span>{" "}
          <Link className="italic" to={publicUrls.quizBankMechanical}>
            Practice Hub (Mechanical) for 6 Months!
          </Link>
          !
        </p>
      </div>
      <div className="flex justify-center items-center">
        {/* {userData ? ( */}
        <div className="container mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 p-4 sm:p-6">
          {data?.data?.liveClassSubscription?.length
            ? data.data.liveClassSubscription.map((item) => {
              return (
                <div
                  key={item._id}
                  className="bg-white shadow-lg hover:shadow-xl rounded-2xl p-4 flex flex-col h-full transition-all duration-300"
                >
                  <button className="block w-full flex justify-center">
                    {!item?.image ? (
                      <img
                        src="/fileDownload.png"
                        alt={item.title}
                        className="w-32 h-32 object-cover transform group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-32 h-32 object-cover rounded-xl"
                      />
                    )}
                  </button>
                  <div className="flex flex-col items-center text-center mt-4 gap-1 flex-1">
                    <h2 className="text-base font-semibold line-clamp-2 h-12">
                      {item.title}
                    </h2>
                    <p className="text-base font-semibold text-primary">
                      ${item.price}
                    </p>

                    {/* Duration with a subtle but clear format */}
                    {item?.startDate && item?.endDate && (
                      <p className="text-xs text-gray-500 italic">
                        {getLiveClassStatus(item.startDate, item.endDate)}
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4 w-full">
                      {item?.purchased ? (
                        <Link to={studentUrls.liveClassMechanical} className="w-full">
                          <Button
                            size="small"
                            className="w-full justify-center min-h-[46px]"
                          >
                            Dashboard
                          </Button>
                        </Link>
                      ) : (
                        <Button
                          size="small"
                          className="w-full justify-center min-h-[46px]"
                          onClick={() =>
                            !userData ? navigate("/login") : onBuyClick(item)
                          }
                          disabled={
                            isMutating ||
                            getLiveClassStatus(
                              item.startDate,
                              item.endDate
                            ).includes("Classes finished on")
                          }
                        >
                          {item?.actionButton || "Buy Now"}
                        </Button>
                      )}
                      <Button
                        size="small"
                        className="w-full justify-center min-h-[46px]"
                        onClick={() => setRoutineModal(item.routine)}
                      >
                        <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                        Schedule
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
            : null}
        </div>
        {/* ) : (
          <Link to="/login" className="mt-6">
            <Button type="primary" className="w-full">
              Login
            </Button>
          </Link>
        )} */}
      </div>
      <Modal
        isOpen={isModalOpen}
        title={"Payment"}
        onCancel={() => {
          setIsModalOpen(false);
          setSelected(null);
          setPaymentConfirmScreen(false);
          setPromoCode("");
          setDiscountedPrice(null);
          setAppliedPromo("");
        }}
        isLoading={isMutating}
        formWidth="sm:max-w-[50%]"
        footer={false}
      >
        {paymentConfirmScreen && selected && (
          <div className="space-y-4">
            {/* Price Display */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:w-1/2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">
                    Total Price
                  </span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {discountedPrice ? (
                    <>
                      <span className="line-through text-gray-400 mr-2">
                        ${selected.price}
                      </span>
                      <span className="text-green-600">${discountedPrice}</span>
                    </>
                  ) : (
                    <span>${selected.price}</span>
                  )}
                </div>
              </div>
              {discountedPrice && (
                <p className="text-xs text-gray-500">
                  A promo code has been applied. You saved{" "}
                  <span className="font-semibold text-green-600">
                    ${parseFloat((selected.price - discountedPrice).toFixed(2))}
                  </span>
                  !
                </p>
              )}
            </div>

            {/* Promo Code Input with Apply Button */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Promo Code
              </label>
              <div className="flex flex-wrap gap-2">
                <Input
                  type="text"
                  value={promoCode}
                  onChange={(_, value) => setPromoCode(value)}
                  placeholder="Enter promo code"
                  className="flex-1 p-2"
                />
                <Button
                  type="button"
                  size="small"
                  className="bg-primary text-white text-center my-2"
                  onClick={(e) => {
                    handleApplyPromo(e);
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              By purchasing, you agree to our{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary font-medium"
              >
                terms & conditions
              </a>
              .
            </p>
            <Button
              type="button"
              className="bg-primary text-white text-center"
              onClick={() => {
                onBuy(selected);
              }}
            >
              Purchase
            </Button>
          </div>
        )}

        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              onClose={() => {
                setIsModalOpen(false);
                setClientSecret("");
              }}
              clientSecret={clientSecret}
              mutate={mutate}
              item={selected}
            />
          </Elements>
        )}
      </Modal>
      <Modal
        isOpen={routineModal !== null}
        title={"View Routine"}
        // onConfirm={handleSubmit}
        onCancel={() => {
          setRoutineModal(null);
        }}
        footer={false}
      >
        <RoutineView routineModal={routineModal} />
      </Modal>
    </motion.div>
  );
};

export default LiveClassMechanical;
